import {
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link,
  Image,
  Minus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  onClick: () => void;
  active?: boolean;
}

function ToolbarButton({ icon, label, shortcut, onClick, active }: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClick}
          className={cn(
            "h-8 w-8 text-muted-foreground hover:text-foreground",
            active && "bg-muted text-foreground"
          )}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <span>{label}</span>
        {shortcut && <span className="ml-2 text-muted-foreground text-xs">{shortcut}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

interface MilkdownToolbarProps {
  onCommand: (command: string) => void;
  onImageUpload?: () => void;
  className?: string;
}

export function MilkdownToolbar({ onCommand, onImageUpload, className }: MilkdownToolbarProps) {
  return (
    <div className={cn(
      "flex items-center gap-0.5 p-1.5 border-b border-border bg-card/50 flex-wrap",
      className
    )}>
      {/* Text formatting */}
      <ToolbarButton
        icon={<Bold className="h-4 w-4" />}
        label="Bold"
        shortcut="Ctrl+B"
        onClick={() => onCommand('toggleBold')}
      />
      <ToolbarButton
        icon={<Italic className="h-4 w-4" />}
        label="Italic"
        shortcut="Ctrl+I"
        onClick={() => onCommand('toggleItalic')}
      />
      <ToolbarButton
        icon={<Strikethrough className="h-4 w-4" />}
        label="Strikethrough"
        onClick={() => onCommand('toggleStrikethrough')}
      />
      <ToolbarButton
        icon={<Code className="h-4 w-4" />}
        label="Inline Code"
        onClick={() => onCommand('toggleInlineCode')}
      />

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Headings */}
      <ToolbarButton
        icon={<Heading1 className="h-4 w-4" />}
        label="Heading 1"
        onClick={() => onCommand('heading1')}
      />
      <ToolbarButton
        icon={<Heading2 className="h-4 w-4" />}
        label="Heading 2"
        onClick={() => onCommand('heading2')}
      />
      <ToolbarButton
        icon={<Heading3 className="h-4 w-4" />}
        label="Heading 3"
        onClick={() => onCommand('heading3')}
      />

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Lists */}
      <ToolbarButton
        icon={<List className="h-4 w-4" />}
        label="Bullet List"
        onClick={() => onCommand('bulletList')}
      />
      <ToolbarButton
        icon={<ListOrdered className="h-4 w-4" />}
        label="Numbered List"
        onClick={() => onCommand('orderedList')}
      />
      <ToolbarButton
        icon={<Quote className="h-4 w-4" />}
        label="Blockquote"
        onClick={() => onCommand('blockquote')}
      />

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Links and media */}
      <ToolbarButton
        icon={<Link className="h-4 w-4" />}
        label="Insert Link"
        onClick={() => onCommand('link')}
      />
      {onImageUpload && (
        <ToolbarButton
          icon={<Image className="h-4 w-4" />}
          label="Insert Image"
          onClick={onImageUpload}
        />
      )}
      <ToolbarButton
        icon={<Minus className="h-4 w-4" />}
        label="Horizontal Rule"
        onClick={() => onCommand('hr')}
      />
    </div>
  );
}
